/// Soft-trial sign-in nudge for anonymous players.
///
/// Conversion lever: encourage guests to upgrade to Google
/// without ever blocking play. After a small number of finished
/// runs as anonymous, a one-time modal points them at sign-in.
/// All state lives in localStorage so it travels per-browser
/// and resets if the browser cache is cleared.
///
/// We never reset the count when the user signs in — counters are
/// per-browser, not per-user. If a signed-in user later signs out
/// and continues anonymously, the existing count is fine; the
/// nudge gate (shown flag + signed-in check) prevents re-showing.

import type { User } from 'firebase/auth'

const COUNT_KEY = 'flatout.web.anonymousRuns'
const SHOWN_KEY = 'flatout.web.signinNudgeShown.v1'
const NUDGE_THRESHOLD = 5

function readNumber(key: string): number {
  const raw = localStorage.getItem(key)
  const n = raw ? Number.parseInt(raw, 10) : 0
  return Number.isFinite(n) && n >= 0 ? n : 0
}

/// Bump the anonymous-run counter when a finished session belongs
/// to an anonymous user. No-op for signed-in users.
export function recordRunIfAnonymous(user: User | null): void {
  if (!user || !user.isAnonymous) return
  const next = readNumber(COUNT_KEY) + 1
  localStorage.setItem(COUNT_KEY, String(next))
}

export function anonymousRunCount(): number {
  return readNumber(COUNT_KEY)
}

/// True when the player should see the sign-in nudge: still
/// anonymous, hit the threshold, hasn't seen it yet.
export function shouldShowSigninNudge(user: User | null): boolean {
  if (!user || !user.isAnonymous) return false
  if (localStorage.getItem(SHOWN_KEY) === '1') return false
  return readNumber(COUNT_KEY) >= NUDGE_THRESHOLD
}

export function markSigninNudgeShown(): void {
  localStorage.setItem(SHOWN_KEY, '1')
}

export const SIGNIN_NUDGE_THRESHOLD = NUDGE_THRESHOLD
